import React, {DetailedHTMLProps, HTMLAttributes, ReactNode, useEffect, useRef, useState} from "react";
import {DraggableCore, DraggableEventHandler} from "react-draggable";
import {useStopScroll} from "../../hooks/window";
import {constrain} from "../../common/math";
import store, {useSelectedNodes, useTypedSelector} from "../../store";
import {setTranslate, setZoomTranslate} from "../../store/workspace";
import {useStyle} from "../../hooks";
import {AnimatePresence, motion} from "framer-motion";
import {_scale} from "../nodes";
import {isExecutableNode, Node, setMode} from "../../store/nodes";
import RectangleSelector from "./RectangleSelector";
import FancyGrid from "./FancyGrid";
import FancyIndicator from "./FancyIndicator";
import ColorWheel from "../controllers/input/ColorWheel";

export type ScreenBox = { x: number, y: number, width: number, height: number }
export type ZoomTranslateState = { pos: {x: number, y: number}, zoom: number, screenBox: ScreenBox }

export type RectangleSelection = { x1: number, y1: number, x2: number, y2: number }
export const hasRectangle = (rect: RectangleSelection | null): rect is RectangleSelection => {
    return rect !== null && rect.x1 !== rect.x2 && rect.y1 !== rect.y2;
}

export const getNodesByRectangle = (nodes: { [key: string]: Node }, rect: RectangleSelection) => {
    let {x1, y1, x2, y2} = rect;
    let nodes_ = Object.values(nodes);
    return nodes_.filter(node => {
        if (isExecutableNode(node)) {
            let {x, y, width, height} = node.shape;
            return x > Math.min(x1, x2) && x + width < Math.max(x1, x2) && y > Math.min(y1, y2) && y + height < Math.max(y1, y2);
        }
        return false;
    });
}

const ZoomTranslate = ({children, onZoomChange, grid, ...props}: {
    children: ReactNode,
    minZoom?: number,
    maxZoom?: number,
    defaultZoom?: number,
    onZoomChange?: (zoom: number) => any,
    disable?: boolean,
    grid?: boolean
    paneProps?: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>
}) => {
    const dragRef = useRef<HTMLDivElement>(null)
    const zt = useZoomTranslate();
    const [button, setButton] = useState<number | null>(null);
    const [rect, setRect] = useState<RectangleSelection | null>(null);
    const [result, setResult] = useState<RectangleSelection | null>(null);
    const [, setSelected] = useSelectedNodes();

    useEffect(() => {
        if (result) {
            let nodes = getNodesByRectangle(store.getState().nodes.nodes, result);
            setSelected(nodes.map(node => node.id));
        }
    }, [result])

    const style = useStyle();

    const minZoom = props.minZoom ?? 0.1875;
    const maxZoom = props.maxZoom ?? 3;

    const scrollRef = useStopScroll();
    const scale = (x: number) => x / zt.zoom / _scale;

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        const delta = e.deltaY < 0 ? e.deltaY * (1 - 11/10) : e.deltaY * (10/11 - 1);
        console.log(e.deltaY, delta)
        const newScale = constrain(zt.zoom * (1 + delta / 100), minZoom, maxZoom);

        const ratio = 1 - newScale / zt.zoom;

        store.dispatch(setZoomTranslate(
            zt.x + (e.clientX - zt.x) * ratio,
            zt.y + (e.clientY - zt.y) * ratio,
            newScale
        ));

        onZoomChange && onZoomChange(newScale);
    };

    const handleStart: DraggableEventHandler = (e, data) => {
        if ("button" in e) {
            setButton(e.button);
            if (e.button === 0) {
                setRect({x1: data.x, x2: data.x, y1: data.y, y2: data.y});
            }
        }
    }

    const handleDrag: DraggableEventHandler = (e, data) => {
        if (props.disable) return;
        if (button === 0) {
            setRect(rect => ({...(rect ?? {x1: data.x, y1: data.y}), x2: data.x, y2: data.y}));
            store.dispatch(setMode('select'));
        } else {
            store.dispatch(setTranslate(zt.x + data.deltaX, zt.y + data.deltaY));
        }
    }

    const handleStop: DraggableEventHandler = (e, data) => {
        if (rect) {
            store.dispatch(setMode('default'));
        }

        setResult(rect ? {
            x1: scale(rect.x1) - zt.x / zt.zoom / _scale,
            y1: scale(rect.y1) - zt.y / zt.zoom / _scale,
            x2: scale(rect.x2) - zt.x / zt.zoom / _scale,
            y2: scale(rect.y2) - zt.y / zt.zoom / _scale
        } : null);

        setRect(null);
    }

    const handlePreventContextMenu = (e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()

    return <div
        {...props.paneProps}
        ref={scrollRef}
        style={{
            ...props.paneProps?.style,
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden'
        }}
        onWheelCapture={handleWheel}
        onContextMenu={handlePreventContextMenu}
    >
        <DraggableCore allowAnyClick nodeRef={dragRef} onStart={handleStart} onDrag={handleDrag} onStop={handleStop}>
            <div ref={dragRef} style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                zIndex: 0
            }}/>
        </DraggableCore>
        <AnimatePresence>{grid &&
            <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}>
                <FancyGrid/>
            </motion.div>}
        </AnimatePresence>
        <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            transformOrigin: "0 0",
            transform: `translate(${zt.x}px, ${zt.y}px) scale(${zt.zoom}`,
            zIndex: 100
        }}>
            {!rect && <RectangleSelector initialBox={result}/>}
            {children}
        </div>
        {rect && hasRectangle(rect) && <div style={{
            position: 'absolute',
            left: Math.min(rect.x1, rect.x2),
            top: Math.min(rect.y1, rect.y2),
            width: Math.max(rect.x1, rect.x2) - Math.min(rect.x1, rect.x2) - 2,
            height: Math.max(rect.y1, rect.y2) - Math.min(rect.y1, rect.y2) - 2,
            border: '2px solid ' + style.palette.highlight.ghost
        }}/>}
        <FancyIndicator strokeWidth={5}/>
    </div>
}

export default ZoomTranslate;

export const useZoomTranslate: () => {x: number, y: number, zoom: number} = () => {
    let zt = useTypedSelector(state => state.transform); //useContext<ZoomTranslateState>(ZoomTranslateContext);
    return {x: zt.pos.x, y: zt.pos.y, zoom: zt.zoom};
}

export const useZoom: () => number = () => {
    return useTypedSelector(state => state.transform.zoom);//useContext<ZoomTranslateState>(ZoomTranslateContext).zoom;
}

export const useScreenBox: () => ScreenBox = () => {
    return useTypedSelector(state => state.transform.screenBox);//useContext<ZoomTranslateState>(ZoomTranslateContext).zoom;
}

export const useIsExecutableNodeOutOfScreen: (id: string) => boolean = (id: string) => {
    return useTypedSelector(state => {
        let box = state.transform.screenBox;
        let node = state.nodes.nodes[id];
        if (!node) return true;
        if (!isExecutableNode(node)) return true;

        return (node.shape.x + node.shape.width) * _scale < box.x
            || (node.shape.y + node.shape.height) * _scale < box.y
            || node.shape.x * _scale > box.x + box.width
            || node.shape.y * _scale > box.y + box.height;
    });
}